import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

function Spinner() {
  return (
    <div className="flex justify-center py-8">
      <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export default function SubscriptionPage() {
  const queryClient = useQueryClient();

  // ── Fetch plans ──
  const { data: plansData, isLoading: loadingPlans } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => api.get('/subscriptions/plans').then((r) => r.data),
  });

  // ── Fetch current subscription ──
  const { data: currentData, isLoading: loadingCurrent } = useQuery({
    queryKey: ['subscription-current'],
    queryFn: () => api.get('/subscriptions/me').then((r) => r.data),
  });

  // ── Subscribe mutation ──
  const subscribeMutation = useMutation({
    mutationFn: (plan: string) => api.post('/subscriptions/subscribe', { plan }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-current'] });
    },
  });

  // ── Cancel mutation ──
  const cancelMutation = useMutation({
    mutationFn: () => api.post('/subscriptions/cancel'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-current'] });
    },
  });

  if (loadingPlans || loadingCurrent) return <Spinner />;

  const plans = plansData?.plans ?? [];
  const currentSub = currentData?.subscription;

  const planColors: Record<string, string> = {
    base: 'from-blue-500/10 to-blue-600/5 border-blue-200 dark:border-blue-800',
    supporter: 'from-purple-500/10 to-purple-600/5 border-purple-200 dark:border-purple-800',
    champion: 'from-amber-500/10 to-amber-600/5 border-amber-200 dark:border-amber-800',
  };

  const planIcons: Record<string, string> = {
    base: '�️',
    supporter: '💎',
    champion: '👑',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-900 dark:text-white">💳 Subscription</h1>
        <p className="text-surface-500 dark:text-surface-400 mt-1">
          Sostieni il Movimento del Dono con un micro-dono mensile ricorrente
        </p>
      </div>

      {/* Current subscription */}
      {currentSub && (
        <div className="card p-6 bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-surface-900 dark:text-white">
                {planIcons[currentSub.plan] ?? '📋'} Piano attivo: {currentSub.plan_name}
              </h2>
              <p className="text-sm text-surface-500 dark:text-surface-400 mt-1">
                €{currentSub.amount}/mese — {currentSub.days_remaining} giorni rimanenti
              </p>
              <p className="text-xs text-surface-400 dark:text-surface-500 mt-1">
                Periodo: {new Date(currentSub.period_start).toLocaleDateString('it-IT')} → {new Date(currentSub.period_end).toLocaleDateString('it-IT')}
              </p>
            </div>
            <button
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              className="btn-ghost text-red-500 hover:text-red-600 px-4 py-2 rounded-xl text-sm"
            >
              {cancelMutation.isPending ? '...' : 'Cancella'}
            </button>
          </div>
          {/* Benefits */}
          <ul className="mt-3 space-y-1">
            {(currentSub.benefits ?? []).map((b: string, i: number) => (
              <li key={i} className="text-sm text-surface-600 dark:text-surface-300 flex items-center gap-2">
                <span className="text-green-500">✓</span> {b}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Revenue split info */}
      <div className="card p-4 bg-surface-50 dark:bg-surface-800/50">
        <h3 className="text-sm font-semibold text-surface-700 dark:text-surface-300 mb-2">
          Come viene utilizzato il tuo contributo
        </h3>
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-500 rounded-full" />
            <span className="text-surface-600 dark:text-surface-400">60% Fondo stabilizzatore</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
            <span className="text-surface-600 dark:text-surface-400">30% Pool payout</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-amber-500 rounded-full" />
            <span className="text-surface-600 dark:text-surface-400">10% Fee operativa</span>
          </div>
        </div>
      </div>

      {/* Plans grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan: any) => {
          const isActive = currentSub?.plan === plan.id;
          return (
            <div
              key={plan.id}
              className={`card p-6 bg-gradient-to-br ${planColors[plan.id] ?? ''} border transition-all ${
                isActive ? 'ring-2 ring-primary-500' : ''
              }`}
            >
              <div className="text-center mb-4">
                <span className="text-3xl">{planIcons[plan.id] ?? '📋'}</span>
                <h3 className="text-xl font-bold text-surface-900 dark:text-white mt-2">{plan.name}</h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-surface-900 dark:text-white">€{plan.amount}</span>
                  <span className="text-surface-500 dark:text-surface-400">/mese</span>
                </div>
              </div>

              <ul className="space-y-2 mb-6">
                {(plan.benefits ?? []).map((b: string, i: number) => (
                  <li key={i} className="text-sm text-surface-600 dark:text-surface-300 flex items-center gap-2">
                    <span className="text-green-500">✓</span> {b}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => subscribeMutation.mutate(plan.id)}
                disabled={subscribeMutation.isPending || isActive}
                className={`w-full py-2 rounded-xl text-sm font-semibold transition-colors ${
                  isActive
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 cursor-default'
                    : 'btn-primary disabled:opacity-50'
                }`}
              >
                {isActive ? '✅ Piano attivo' : subscribeMutation.isPending ? '...' : 'Scegli piano'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Disclaimer */}
      <div className="card p-4 text-sm text-surface-500 dark:text-surface-400 bg-surface-50 dark:bg-surface-800/50">
        <p>
          ⚠️ La subscription è un <strong>micro-dono volontario ricorrente</strong> — non un investimento.
          Non garantisce alcun rendimento. Puoi cancellare in qualsiasi momento.
          Il tuo contributo sostiene la stabilità del sistema per tutti i partecipanti.
        </p>
      </div>
    </div>
  );
}
